import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { JobView, JobWarning, StepView } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { StatusChip } from '@/components/ops/Badges';
import { KeyValues, PageHeader, Section } from '@/components/ops/Layout';
import { RemovalPanel } from './RemovalPanel';
import { ConfirmDialog, RevealOnce } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { CodeChip, MutationError, QueryState } from '@/components/ops/Problem';
import { formatCredits } from '@/ops/credits';
import { formatTimestamp, shortId } from '@/ops/format';
import { REPLICA } from '@/ops/live';
import { jobStatusText, stepAdvice } from '@/ops/stepAdvice';
import { PHASE_NAMES, groupSteps, phaseState, stepName, type PhaseState } from '@/ops/stepNames';
import { formatUntil, stepReasonLabel } from '@/ops/stepReasons';
import { useCreditScale } from '@/ops/useCreditScale';
import { formatWhen } from '@/pages/AccountsPage';

const jobKey = (id: string) => ['ops', 'job', id] as const;

const RETRYABLE_STEP: StepView['status'][] = ['failed', 'waiting_retry', 'awaiting', 'skipped'];
const UNFINISHED: JobView['status'][] = ['pending', 'running', 'failed'];

/** The page title by what the job does (2026-10-02: setups, deletions and the clean-up of a cancelled setup). */
export function jobTitle(j: Pick<JobView, 'op' | 'label'>): string {
  switch (j.op) {
    case 'delete': return `Deleting ${j.label}`;
    case 'discard': return `Removing what the setup of ${j.label} created`;
    default: return `Setting up ${j.label}`;
  }
}

/**
 * Whether the page offers Cancel. A setup: while unfinished. A deletion: never — it is stopped with Stop (reject), which
 * unfreezes the tenant first. A clean-up: only before its freeze ran (the server refuses later).
 */
export function canCancel(j: Pick<JobView, 'op' | 'status' | 'steps'>): boolean {
  if (!UNFINISHED.includes(j.status)) return false;
  if (j.op === 'delete') return false;
  if (j.op === 'discard') {
    const freeze = (j.steps ?? []).find((s) => s.key === 'discard.freeze');
    return !freeze || (freeze.status === 'pending' && freeze.attempts === 0);
  }
  return true;
}

/** The retry action's wording follows what the API re-runs for this status (docs §4.2). */
function retryLabel(status: JobView['status']): string {
  if (status === 'done') return 'Re-run every step';
  if (status === 'done_with_warnings') return 'Retry the warned steps';
  if (status === 'running') return 'Try again now';
  return 'Retry the setup';
}

/**
 * Whether the header's job retry would be ACCEPTED — the same rules as the API (ProvisioningJobStore, RetryJob), so the
 * page never offers a button that answers 409: never on a cancelled job; a finished job re-runs; a failed one retries what
 * stopped; a running one only skips the wait of a step that is waiting for its next try (and never while a step runs).
 */
export function canRetryJob(job: Pick<JobView, 'status' | 'steps'>): boolean {
  const steps = job.steps ?? [];
  switch (job.status) {
    case 'done':
    case 'failed':
      return true;
    case 'running':
      return !steps.some((s) => s.status === 'running')
        && steps.some((s) => s.status === 'waiting_retry' || s.status === 'awaiting');
    default:
      return false; // pending: nothing ran yet · cancelled: create a new setup · done_with_warnings: the warnings panel
  }
}

/**
 * Whether one step's retry would be accepted (ProvisioningJobStore, RetryStep): not on a cancelled job, not while it runs,
 * and not behind an EARLIER failed step (it would wait there for ever — that step is retried first).
 */
export function canRetryStep(job: Pick<JobView, 'status' | 'steps'>, step: StepView): boolean {
  if (job.status === 'cancelled' || step.status === 'running') return false;
  if (!RETRYABLE_STEP.includes(step.status) && step.warning !== true) return false;
  return !(job.steps ?? []).some((s) => s.ordinal < step.ordinal && s.status === 'failed');
}

/**
 * One provisioning job: its step timeline (status, attempts, HTTP status, error code, the next retry,
 * an awaiting step's deadline), the admin actions (retry job / warned steps / one step, cancel), the
 * warnings of a job that finished with some, and — when IAM generated a founder password — the one-time
 * reveal. The page polls while the job runs; the runner does the work server-side.
 */
export function ProvisioningJobPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const job = useQuery({
    queryKey: jobKey(id),
    queryFn: () => opsApi.provisioning.getJob(id),
    ...REPLICA,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'pending' ? 5_000 : false;
    },
  });

  const [confirm, setConfirm] = useState<'retry' | 'cancel' | 'reveal' | null>(null);
  const [password, setPassword] = useState<{ value: string; email: string | null; tenantId: string } | null>(null);

  const apply = (updated: JobView) => queryClient.setQueryData(jobKey(id), updated);

  const retryJob = useMutation({ mutationFn: () => opsApi.provisioning.retryJob(id), onSuccess: (j) => { apply(j); setConfirm(null); } });
  const cancelJob = useMutation({ mutationFn: () => opsApi.provisioning.cancelJob(id), onSuccess: (j) => { apply(j); setConfirm(null); } });
  const retryStep = useMutation({ mutationFn: (key: string) => opsApi.provisioning.retryStep(id, key), onSuccess: apply });
  // The password answer never enters a cache: gcTime 0, reset() on success, moved into local state
  // that is discarded when the reveal dialog closes.
  const reveal = useMutation({ mutationFn: () => opsApi.provisioning.revealPassword(id), gcTime: 0 });

  return (
    <RequirePermission permission={PERMISSIONS.tenantProvision} what="Provisioning jobs">
      <QueryState query={job}>
        {(j) => (
          <>
            <PageHeader
              back={{ to: '/ops/provisioning', label: 'Setup jobs' }}
              title={jobTitle(j)}
              subtitle={
                <span className="row">
                  <StatusChip status={j.status} />
                  <span>{j.kind === 'external' ? 'External account' : 'Tenant'} on {j.environment}</span>
                  <span>· started {formatWhen(j.created_at)} by {j.created_by.name}</span>
                  <span>· updated {formatWhen(j.updated_at)}</span>
                </span>
              }
              actions={
                <>
                  {/* done_with_warnings: the warnings panel below carries its retry — one button, not two. */}
                  {canRetryJob(j) && (
                    <button type="button" className="btn btn-ghost" onClick={() => setConfirm('retry')}>{retryLabel(j.status)}</button>
                  )}
                  {canCancel(j) && (
                    <button type="button" className="btn btn-ghost is-danger" onClick={() => setConfirm('cancel')}>
                      {j.op === 'discard' ? 'Cancel the clean-up' : j.status === 'failed' ? 'Cancel and remove' : 'Cancel the setup'}
                    </button>
                  )}
                </>
              }
            />

            <JobProgress job={j} onRetryStep={(key) => retryStep.mutate(key)}
              pendingStep={retryStep.isPending ? retryStep.variables ?? null : null} />

            {cancelJob.data?.tenant_removal && (
              <div className="card card-body" data-testid="cleanup-link">
                {cancelJob.data.tenant_removal.job_id ? (
                  <span className="t-body">
                    What this setup created is being removed by a{' '}
                    <Link to={`/ops/provisioning/jobs/${cancelJob.data.tenant_removal.job_id}`}>clean-up job</Link>. The short name and the
                    owner's e-mail are free again once it finishes.
                  </span>
                ) : (
                  <span className="t-body note-warning">
                    The clean-up did not start: {cancelJob.data.tenant_removal.message ?? cancelJob.data.tenant_removal.code ?? 'unknown reason'}
                  </span>
                )}
              </div>
            )}

            {(j.op === 'delete' || j.op === 'discard') && <RemovalPanel job={j} />}

            <WarningsPanel job={j} onRetryWarned={() => setConfirm('retry')}
              onRetryStep={(key) => retryStep.mutate(key)} pendingStep={retryStep.isPending ? retryStep.variables ?? null : null} />
            {retryStep.error && (
              <div className="card card-body"><MutationError error={retryStep.error} /></div>
            )}

            <PasswordPanel job={j} onReveal={() => setConfirm('reveal')} />

            <Section
              title="Steps"
              meta={`${(j.steps ?? []).filter((s) => s.status === 'done' || s.status === 'skipped').length} of ${(j.steps ?? []).length} finished`}
              flush
            >
              {groupSteps(j.steps ?? []).map((group, index) => {
                const state = phaseState(group.steps);
                // A one-step phase named like its step ("Welcome email") needs no heading of its own.
                const lone = group.steps.length === 1 && stepName(group.steps[0]!.key) === PHASE_NAMES[group.phase];
                return (
                  <div key={`${group.phase}-${index}`} className="phase" data-phase={group.phase}>
                    <div className="phase-head" hidden={lone}>
                      <span className={`step-dot is-${PHASE_DOT[state]}`} aria-hidden="true" />
                      <strong className="t-heading">{PHASE_NAMES[group.phase]}</strong>
                      <span className="t-caption muted">{PHASE_WORDS[state]}</span>
                    </div>
                    <ol className="step-list">
                      {group.steps.map((s) => (
                        <StepRow key={s.key} step={s} current={j.current_step === s.key} jobStatus={j.status}
                          canRetry={canRetryStep(j, s)}
                          pending={retryStep.isPending && retryStep.variables === s.key}
                          onRetry={() => retryStep.mutate(s.key)} />
                      ))}
                    </ol>
                  </div>
                );
              })}
            </Section>

            <JobSection job={j} />

            {confirm === 'retry' && (
              <ConfirmDialog title={j.status === 'done' ? 'Re-run every step?' : j.status === 'done_with_warnings' ? 'Retry the warned steps?' : 'Retry the job?'}
                confirmLabel={retryLabel(j.status)}
                pending={retryJob.isPending} onConfirm={() => retryJob.mutate()} onCancel={() => { setConfirm(null); retryJob.reset(); }}>
                <p className="t-body" style={{ margin: 0 }}>
                  {j.status === 'done'
                    ? 'Every step runs again against the same tenant id. Each step is idempotent, so a finished tenant converges to the same state.'
                    : j.status === 'done_with_warnings'
                      ? `Only the warned step(s) run again (${(j.warnings ?? []).map((w) => w.step).join(', ') || 'see the warnings'}), each with a fresh window. The tenant stays live meanwhile.`
                      : j.status === 'running'
                        ? 'The step waiting for its next try runs now instead of waiting. You become the acting admin for the calls that follow.'
                        : 'The failed, waiting and awaiting steps run again, in order, with the same tenant id. You become the acting admin for the calls that follow.'}
                </p>
                <MutationError error={retryJob.error} />
              </ConfirmDialog>
            )}
            {confirm === 'cancel' && (
              <ConfirmDialog title={j.op === 'discard' ? 'Cancel the clean-up?' : 'Cancel the setup?'} confirmLabel="Cancel job" destructive
                pending={cancelJob.isPending}
                onConfirm={() => cancelJob.mutate()} onCancel={() => { setConfirm(null); cancelJob.reset(); }}>
                <p className="t-body" style={{ margin: 0 }}>
                  {j.op === 'discard'
                    ? 'The clean-up has not started; what the cancelled setup created stays as it is.'
                    : 'Steps that have not run are cancelled. If the setup already reached IAM, a clean-up job then removes what it created '
                      + "(the tenant, its databases, the owner's invitation) so the short name and the owner's e-mail can be used again — "
                      + 'unless someone has already signed in to the tenant: then it is kept, and only a full deletion from its tenant page removes it.'}
                </p>
                {(j.steps ?? []).filter((s) => s.status === 'running').map((s) => (
                  <p key={s.key} className="t-body" style={{ margin: '8px 0 0' }}>
                    “{stepName(s.key)}” is running right now: it finishes first, and what it did is recorded.
                  </p>
                ))}
                <MutationError error={cancelJob.error} />
              </ConfirmDialog>
            )}
            {confirm === 'reveal' && (
              <ConfirmDialog title="Reveal the founder password?" confirmLabel="Reveal once" destructive pending={reveal.isPending}
                onConfirm={() => reveal.mutate(undefined, {
                  onSuccess: (r) => {
                    const value = r.password;
                    reveal.reset();
                    setConfirm(null);
                    setPassword({ value, email: r.founder_email, tenantId: r.tenant_id });
                    queryClient.invalidateQueries({ queryKey: jobKey(id) });
                  },
                })}
                onCancel={() => { setConfirm(null); reveal.reset(); }}>
                <p className="t-body" style={{ margin: 0 }}>
                  The password is shown <strong>once</strong>. After this the console discards it and nobody — you included — can
                  see it again. Have a secure channel ready to hand it to the founder.
                </p>
                <MutationError error={reveal.error} />
              </ConfirmDialog>
            )}
            {password && (
              <RevealOnce title="Founder password" what="founder password" secret={password.value} onDismiss={() => setPassword(null)}>
                <p className="t-caption muted" style={{ margin: 0 }}>
                  For <span className="mono">{password.email ?? 'the founder'}</span> on tenant <span className="mono">{shortId(password.tenantId)}</span>.
                  Ask them to change it at first sign-in.
                </p>
              </RevealOnce>
            )}
          </>
        )}
      </QueryState>
    </RequirePermission>
  );
}

const PHASE_DOT: Record<PhaseState, string> = {
  failed: 'failed', working: 'running', gated: 'pending', waiting: 'pending', warned: 'warned', done: 'done', cancelled: 'cancelled',
};
const PHASE_WORDS: Record<PhaseState, string> = {
  failed: 'stopped', working: 'in progress', gated: 'waiting for a decision or the backup', waiting: 'not started',
  warned: 'done, with a warning', done: 'done', cancelled: 'cancelled',
};

/**
 * The top of the page in words: where the setup is, and — when it stopped — which step, what went wrong and what to
 * do, with the retry right there. A finished setup links to the tenant.
 */
function JobProgress({
  job: j, onRetryStep, pendingStep,
}: {
  job: JobView;
  onRetryStep: (key: string) => void;
  pendingStep: string | null;
}) {
  const steps = j.steps ?? [];
  const finished = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  const percent = steps.length ? Math.round((finished * 100) / steps.length) : 0;
  const failed = steps.find((s) => s.status === 'failed');
  const waiting = steps.find((s) => s.status === 'waiting_retry');
  const current = steps.find((s) => s.key === j.current_step);
  const stuck = failed ?? waiting;
  const advice = stuck ? stepAdvice(stuck) : null;
  const accessUrl = typeof j.result?.access_url === 'string' ? j.result.access_url : null;

  return (
    <div className="card card-body stack-sm" data-testid="job-progress">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong className="t-heading">{jobStatusText(j.status, j.op)}</strong>
        <span className="t-caption muted">{finished} of {steps.length} steps finished</span>
      </div>
      <div className="progress" role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <span className={`progress-fill is-${j.status}`} style={{ width: `${percent}%` }} />
      </div>
      {(j.status === 'running' || j.status === 'pending') && current && !stuck && (
        <span className="t-body">Now: {stepName(current.key)}…</span>
      )}
      {(j.status === 'done' || j.status === 'done_with_warnings') && (j.op === 'delete' || j.op === 'discard') && (
        <span className="t-body">
          The tenant was removed. Its short name and its owner's e-mail can be used for a new tenant.
        </span>
      )}
      {(j.status === 'done' || j.status === 'done_with_warnings') && (j.op ?? 'create') === 'create' && (
        <span className="t-body">
          The tenant is ready.{' '}
          <Link to={`/ops/tenants/${encodeURIComponent(j.environment)}/${j.tenant_id}`}>Open the tenant</Link>
          {accessUrl && <> · <a href={accessUrl} target="_blank" rel="noreferrer noopener">its sign-in page</a></>}
        </span>
      )}
      {stuck && advice && j.status !== 'cancelled' && (
        <div className={`step-advice${stuck.status === 'failed' ? ' is-failed' : ''}`} data-testid="job-advice">
          <strong className="t-heading" style={{ display: 'block' }}>
            {stuck.status === 'failed' ? `“${stepName(stuck.key)}” stopped` : `“${stepName(stuck.key)}” will be tried again`}
          </strong>
          <div className="t-body"><strong>What went wrong:</strong> {advice.problem}</div>
          <div className="t-body"><strong>What to do:</strong> {advice.fix}</div>
          {stuck.status === 'failed' && (
            <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={pendingStep === stuck.key}
              onClick={() => onRetryStep(stuck.key)}>
              {pendingStep === stuck.key ? 'Retrying…' : 'Retry this step'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** The panel's heading: "finished" only once the job has finished — a running job's warnings are "so far". */
export function warningsHeading(status: JobView['status'], count: number): string {
  const n = `${count || 'some'} warning${count === 1 ? '' : 's'}`;
  switch (status) {
    case 'done_with_warnings': return `Finished with ${n} — the tenant is live, but not everything applied`;
    case 'cancelled': return `${n} — the job was cancelled`;
    case 'failed': return `${n} so far — the job failed and waits for a retry`;
    case 'pending':
    case 'running': return `${n} so far — the job is still running`;
    default: return n;
  }
}

/**
 * A job that finished with warnings is NEVER shown as a plain success: the tenant is live and parity is
 * green, but something optional did not apply. Each warning is retryable on its own; "retry warned
 * steps" re-runs all of them.
 */
function WarningsPanel({
  job, onRetryWarned, onRetryStep, pendingStep,
}: {
  job: JobView;
  onRetryWarned: () => void;
  onRetryStep: (key: string) => void;
  pendingStep: string | null;
}) {
  const warnings: JobWarning[] = job.warnings ?? [];
  if (job.status !== 'done_with_warnings' && warnings.length === 0) return null;
  const retryable = (key: string) => {
    const step = (job.steps ?? []).find((s) => s.key === key);
    return step ? canRetryStep(job, step) : job.status !== 'cancelled';
  };

  return (
    <div className="callout callout-warning" role="alert" data-testid="job-warnings">
      <span className="callout-icon" aria-hidden="true">!</span>
      <div className="t-body" style={{ flex: 1, minWidth: 0 }}>
        <strong className="t-heading" style={{ display: 'block' }}>
          {warningsHeading(job.status, warnings.length)}
        </strong>
        <ul className="problem-list">
          {warnings.map((w) => (
            <li key={`${w.step}-${w.code}`} style={{ marginBottom: 8 }}>
              <strong>{stepName(w.step)}</strong> <span className="t-caption mono muted">{w.step}</span> <CodeChip code={w.code} />
              {stepReasonLabel(w.code) && <strong>{stepReasonLabel(w.code)}. </strong>}
              {w.message}{' '}
              {retryable(w.step) && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={pendingStep === w.step} onClick={() => onRetryStep(w.step)}>
                  {pendingStep === w.step ? 'Retrying…' : 'Retry this step'}
                </button>
              )}
            </li>
          ))}
        </ul>
        {job.status === 'done_with_warnings' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={onRetryWarned}>Retry the warned steps</button>
        )}
      </div>
    </div>
  );
}

function JobSection({ job: j }: { job: JobView }) {
  const { scale } = useCreditScale(j.environment);
  const overrides = j.grant_overrides;
  // Billing serializes both keys (null = no override); an omitted key reads as none, never a crash.
  const credits = (micro: number | null | undefined) =>
    typeof micro !== 'number' ? '—' : `${formatCredits(micro, scale)} (${micro.toLocaleString('en-US')} micro-credits)`;

  return (
    <Section title="Details" meta={`created by ${j.created_by.name} · acting admin ${j.acting_admin.name}`}>
      <KeyValues rows={[
        {
          label: 'Tenant id',
          value: (
            <>
              <span className="mono">{j.tenant_id}</span>{' '}
              <Link to={`/ops/tenants/${encodeURIComponent(j.environment)}/${j.tenant_id}`}>open tenant</Link>
            </>
          ),
        },
        { label: 'Industry pack', value: <span className="mono">{j.industry_pack}{j.industry_pack_version !== null ? ` v${j.industry_pack_version}` : ''}</span> },
        { label: 'Plan', value: <span className="mono">{j.plan_code}</span> },
        { label: 'Apps', value: j.apps.join(', ') || '—' },
        { label: 'Hosts', value: j.hosts.join(', ') || 'none' },
        { label: 'Founder', value: j.founder_email ? `${j.founder_email} — ${j.generate_password ? 'generated password' : 'invited'}` : '—' },
        {
          label: 'Grant overrides',
          value: overrides ? `AI ${credits(overrides.ai_credits)} · messaging ${credits(overrides.messaging_credits)}` : 'none (plan and pack defaults)',
        },
        { label: 'Created', value: formatTimestamp(j.created_at) },
        { label: 'Finished', value: j.finished_at ? formatTimestamp(j.finished_at) : j.cancelled_at ? `cancelled ${formatTimestamp(j.cancelled_at)}` : '—' },
        ...(j.result ? [{ label: 'Result', value: <ResultView result={j.result} /> }] : []),
      ]} />
    </Section>
  );
}

function PasswordPanel({ job, onReveal }: { job: JobView; onReveal: () => void }) {
  if (!job.password_generated) return null;
  if (job.password_available) {
    return (
      <div className="callout callout-warning" role="status">
        <span className="callout-icon" aria-hidden="true">!</span>
        <div className="t-body">
          <strong className="t-heading" style={{ display: 'block' }}>A generated founder password is waiting</strong>
          It is held encrypted by the console until an owner reveals it — once. <button type="button" className="btn btn-ghost btn-sm" onClick={onReveal}>Reveal password</button>
        </div>
      </div>
    );
  }
  return (
    <div className="callout callout-info" role="status">
      <span className="callout-icon" aria-hidden="true">i</span>
      <div className="t-body">
        {job.password_revealed_at
          ? `The founder password was revealed ${formatTimestamp(job.password_revealed_at)} and is no longer held by the console.`
          : 'A password will be generated by IAM when the iam.create step runs.'}
      </div>
    </div>
  );
}

function ResultView({ result }: { result: Record<string, unknown> }) {
  const url = typeof result.access_url === 'string' ? result.access_url : null;
  return (
    <div className="stack-sm">
      {url && <a href={url} target="_blank" rel="noreferrer noopener" className="mono">{url}</a>}
      <span className="t-caption">
        {Object.entries(result).filter(([k]) => k !== 'access_url').map(([k, v]) => (
          <span key={k} style={{ marginInlineEnd: 12 }}><span className="muted">{k}</span> <span className="mono">{String(v)}</span></span>
        ))}
      </span>
    </div>
  );
}

/** A `credits.ai` summary names its units (docs §4.5) — shown in words, credits first. */
function CreditsSummary({ summary }: { summary: Record<string, unknown> }) {
  const num = (k: string) => (typeof summary[k] === 'number' ? (summary[k] as number) : null);
  const scale = num('credit_display_scale');
  const amount = num('amount_credits');
  const granted = num('amount_granted_credits');
  const plan = num('plan_grant_credits');
  return (
    <div className="t-caption">
      {amount !== null && <>sent {formatCredits(amount, scale)} (<span className="mono">{amount.toLocaleString('en-US')}</span> µ)</>}
      {typeof summary.source === 'string' && <> · source <span className="mono">{summary.source}</span></>}
      {plan !== null && <> · plan grant {plan.toLocaleString('en-US')} displayed credits</>}
      {scale !== null && <> · scale {scale} ({String(summary.scale_source ?? 'n/a')})</>}
      {granted !== null && <> · granted {formatCredits(granted, scale)}</>}
      {summary.already_applied === true && <> · already applied</>}
    </div>
  );
}

/** The `industry.agents` answer in words (docs §4.6): what deployed, what waits for its KB, what was refused. */
function AgentsSummary({ summary }: { summary: Record<string, unknown> }) {
  const list = (k: string) => (Array.isArray(summary[k]) ? (summary[k] as unknown[]) : []);
  const names = (k: string) => list(k).filter((v): v is string => typeof v === 'string');
  const refused = list('refused').map((r) =>
    r && typeof r === 'object' && 'item' in r ? `${String((r as { item: unknown }).item)} (${String((r as { reason?: unknown }).reason ?? '')})` : String(r));
  return (
    <div className="t-caption" data-testid="agents-summary">
      {names('deployed').length > 0 && <div>deployed: <span className="mono">{names('deployed').join(', ')}</span></div>}
      {names('awaiting_kb').length > 0 && <div>waiting for their knowledge base: <span className="mono">{names('awaiting_kb').join(', ')}</span></div>}
      {refused.length > 0 && <div className="note-error">refused: <span className="mono">{refused.join(', ')}</span></div>}
    </div>
  );
}

/**
 * "Already applied": the service answered that the step's work was already there (HTTP 409 read as done, or a grant's
 * <c>already_applied</c>). Every step is idempotent, so the `idempotent` flag says nothing about THIS answer.
 */
export function alreadyApplied(s: Pick<StepView, 'status' | 'http_status' | 'response_summary'>): boolean {
  if (s.status !== 'done') return false;
  const summary = s.response_summary as Record<string, unknown> | null | undefined;
  return s.http_status === 409 || summary?.already_applied === true || summary?.note === 'already applied';
}

function StepRow({
  step: s, current, jobStatus, canRetry, pending, onRetry,
}: {
  step: StepView;
  current: boolean;
  jobStatus: JobView['status'];
  canRetry: boolean;
  pending: boolean;
  onRetry: () => void;
}) {
  const summary = s.response_summary;
  const hasSummary = !!summary && typeof summary === 'object' && Object.keys(summary).length > 0;
  const isCreditsStep = s.key.startsWith('credits.') && hasSummary;
  const isAgentsStep = s.key === 'industry.agents' && hasSummary;
  const warned = s.warning === true;
  const label = stepReasonLabel(s.error_code);
  const advice = stepAdvice(s);

  return (
    <li className="step-item" aria-current={current ? 'step' : undefined} data-step={s.key}>
      <span className={`step-dot is-${warned ? 'warned' : s.status}`} aria-hidden="true" />
      <div style={{ minWidth: 0 }}>
        <div className="row">
          <span style={{ fontWeight: 600 }}>{stepName(s.key)}</span>
          <StatusChip status={s.status} />
          {warned && (
            <StatusChip
              status="warning"
              tone="warning"
              title={`${s.status === 'done' ? 'Done' : 'Skipped'} with a warning — listed in the job's warnings.`}
            />
          )}
          {alreadyApplied(s) && <StatusChip status="already applied" tone="muted" title="The service answered that this was already in place." />}
          {current && <span className="t-caption subtle">current</span>}
        </div>
        <div className="step-meta t-caption">
          <span className="mono">{s.key}</span>
          {s.attempts > 0 && <span>{s.attempts} attempt{s.attempts === 1 ? '' : 's'}</span>}
          {s.http_status !== null && <span>HTTP {s.http_status}</span>}
          {s.started_at && <span>started {formatTimestamp(s.started_at)}</span>}
          {s.finished_at && <span>finished {formatTimestamp(s.finished_at)}</span>}
          {s.status === 'waiting_retry' && s.next_attempt_at && jobStatus === 'running' && <span>next attempt {formatTimestamp(s.next_attempt_at)}</span>}
          {s.request_hash && <span title={s.request_hash}>request <span className="mono">{s.request_hash.slice(0, 8)}</span></span>}
        </div>

        {s.status === 'awaiting' && (
          <div className="t-caption" style={{ marginTop: 4 }} data-testid="awaiting">
            <strong>Awaiting</strong> — gives up <strong>{formatUntil(s.awaiting_until)}</strong>
            {s.awaiting_until && <> (at {formatTimestamp(s.awaiting_until)})</>}
            {s.next_attempt_at && jobStatus === 'running' && <> · next check {formatTimestamp(s.next_attempt_at)}</>}
            {jobStatus === 'running'
              ? '. Later steps run on meanwhile; the job finishes when this resolves.'
              : '. The setup stopped at another step; this check continues when the setup is retried.'}
          </div>
        )}

        {s.status === 'gated' && (
          <div className="t-caption" style={{ marginTop: 4 }} data-testid="gated">
            <strong>Waiting</strong> — {s.error ?? 'for a decision'}
            {s.awaiting_until && <> · until {formatTimestamp(s.awaiting_until)}</>}. Nothing after this step runs before it is done.
          </div>
        )}

        {/* A warning on a DONE step (iam.create: a non-fatal IAM sub-step failed) reads as a warning, never an error. */}
        {s.status === 'skipped' || s.status === 'awaiting' || warned ? (
          (s.error || s.error_code) && (
            <div className={`t-caption ${warned ? 'note-warning' : 'muted'}`} style={{ marginTop: 4 }} data-testid="step-reason">
              {s.status === 'skipped' ? 'skipped: ' : s.status === 'awaiting' ? 'waiting: ' : 'warning: '}
              {s.error_code && <CodeChip code={s.error_code} />}
              {label && <strong>{label}. </strong>}
              {s.error}
            </div>
          )
        ) : s.status !== 'gated' && (s.error || s.error_code) && (
          <div className="step-error t-caption">
            {advice && (
              <span style={{ display: 'block', color: 'var(--text-main)' }}>
                <strong>What went wrong:</strong> {advice.problem} <strong>What to do:</strong> {advice.fix}
              </span>
            )}
            {s.error_code && <CodeChip code={s.error_code} />}
            {label && <strong>{label}. </strong>}
            {s.error}
          </div>
        )}

        {isCreditsStep && <CreditsSummary summary={summary as Record<string, unknown>} />}
        {isAgentsStep && <AgentsSummary summary={summary as Record<string, unknown>} />}
        {!isCreditsStep && hasSummary && (
          <details className="t-caption">
            <summary>response summary</summary>
            <pre className="pre-json">{JSON.stringify(summary, null, 2)}</pre>
          </details>
        )}
      </div>
      <div>
        {canRetry && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={pending} onClick={onRetry}>
            {pending ? 'Retrying…' : 'Retry step'}
          </button>
        )}
      </div>
    </li>
  );
}
